import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import BootstrapTheme from "@fullcalendar/bootstrap";

/**
 * 月曆(design system 元件)— 包模板 /apps-calendar 的 FullCalendar 組法。
 * 用於合約「維護歷程」分頁(票 04,唯讀)與派工示意月曆(票 15)。
 *
 *   <MonthCalendar events={[{ title:"第1次維護", date:"2026-08-13", color:"#34c38f" }]} />
 *
 * 對照節點:合約 維護歷程分頁=WF_v3 `2375:32205` 內末屏;派工=`DCK-06-02-01`(節點待使用者補連結)。
 * 稿的工具列:‹ › 今日 | 月/週/日 —— 對應
 * headerToolbar 的 prev,next today / dayGridMonth,dayGridWeek,dayGridDay。
 * 預設唯讀;要可互動(派工建單)由呼叫端傳 dateClick/eventClick。
 *
 * `initialDate` 為什麼要額外用 effect 追(票 24):FullCalendar 的 `initialDate`
 * **只在掛載時生效**。呼叫端幾乎都是「先掛空月曆、資料稍後才到」(派工頁、合約頁都是),
 * 掛載當下 events 還是 []、initialDate 還是 undefined,月曆就停在今天所在的月份;
 * 資料到了 FullCalendar 也不會自己回頭跳月 —— 事件明明有,畫面上就是看不到。
 * 2026-09 之前看不出來,因為種子事件正好與「今天」同月;跨月之後派工頁四條測試同時紅。
 * 修在共用層而不是各頁自己 remount,是為了讓兩個呼叫端一次修好。
 * initialDate 落空(該筆沒有任何事件)時不動 —— 停在使用者當下看的月份,不無聲跳回今天。
 */
export const MonthCalendar = ({ events = [], dateClick, eventClick, initialDate }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!initialDate || !ref.current) return;
    ref.current.getApi().gotoDate(initialDate);
  }, [initialDate]);

  return (
    <FullCalendar
      ref={ref}
      plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin]}
      themeSystem="bootstrap"
      initialView="dayGridMonth"
      initialDate={initialDate}
      handleWindowResize
      buttonText={{ today: "今日", month: "月", week: "週", day: "日" }}
      headerToolbar={{
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,dayGridWeek,dayGridDay",
      }}
      events={events}
      editable={false}
      selectable={!!dateClick}
      dateClick={dateClick}
      eventClick={eventClick}
    />
  );
};

MonthCalendar.propTypes = {
  events: PropTypes.array,
  dateClick: PropTypes.func,
  eventClick: PropTypes.func,
  initialDate: PropTypes.string,
};
